import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FocusGroup } from 'components/focus';
import { useSelector, useDispatch } from 'react-redux';
import { openExitDialog } from 'actions/exitDialog';

import appActions from 'common/support/applicationActions';
import { useFocusManager } from 'hooks/focus';
import { useRestoration } from 'hooks/restoration';

import NavItem from 'components/navigation/NavItem';
import FeatureFlagsNavItem from 'components/navigation/FeatureFlagsNavItem';
import { useFeatureFlags } from 'components/providers/FeatureFlagProvider';
import { VideoCtvRedux, KitchenSinkMenuItem } from 'store-types';
import styles from './LeftNav.module.scss';

// const RestorationStatus = {
//   RESTORED: 'restored',
//   STARTED: 'started',
//   UNRESTORED: 'unrestored',
// };
// const useLeftNavRestoration = (isReady, title = "LeftNav") => {
//   console.log("USE LEFT NAV RESTORATION")
//   const { restorationState, setRestorationState } = useRestoration(title);
//   const focusManager = useFocusManager();

//   const restoredFocusId = restorationState?.focusId;

//   const [restorationStatus, setRestorationStatus] = useState(
//     RestorationStatus.STARTED,
//   );

//   useEffect(() => {
//     if (isReady && restorationStatus === RestorationStatus.STARTED) {
//       if (
//         restoredFocusId &&
//         focusManager.getFocusableElement(restoredFocusId)
//       ) {
//         focusManager.setCurrentFocus(restoredFocusId);
//         setRestorationStatus(RestorationStatus.RESTORED);
//       } else {
//         setRestorationStatus(RestorationStatus.UNRESTORED);
//       }
//     }
//   }, [focusManager, isReady, restoredFocusId, restorationStatus]);

//   // update here
//   useEffect(() => {
//     const onFocusChangedListener = () => {
//       if (focusManager.isFocusAncestor(`left-nav-focusable-content`)) {
//         setRestorationState({
//           ...restorationState,
//           focusId: focusManager.currentFocusId,
//         });
//       }
//     };
//     focusManager.addFocusChangedListener(onFocusChangedListener);
//     return () => {
//       focusManager.removeFocusChangedListener(onFocusChangedListener);
//     };
//   }, [focusManager, restorationState, setRestorationState]);

//   return [restorationStatus];
// };
interface PageView {
  page: string;
  type: string | null;
}

interface LeftNavProps {
  focusId?: string | null;
  pageName?: string | null;
}

const LeftNav = (_props: LeftNavProps) => {
  const focusManager = useFocusManager();
  const page = useSelector((state: VideoCtvRedux) => state.pageView) as PageView | null;
  const pageName = page?.page;
  const loading = useSelector((state: VideoCtvRedux) => state.kitchenSink.loading);
  const menu = useSelector((state: VideoCtvRedux) => state.kitchenSink.data.menu);
  const dispatch = useDispatch();

  // Show Feature Flags nav item only in dev mode
  const { isActive } = useFeatureFlags();

  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedState, setExpandedState] = useState(true);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const [visibleState, setVisibleState] = useState(true);
  // Handles the initial focus setting logic
  // const hasFocusBeenInitialized = useRef(false);
  // const [restoredFocus] = useLeftNavRestoration(!loading);
  const fullScreenPlayer = useSelector((state: VideoCtvRedux) => state.hero.fullScreenPlayer);

  const { restorationState } = useRestoration('leftnav');

  useEffect(() => {
    const onFocusChangedListener = () => {
      if (focusManager.isFocusAncestor('left-nav-focusable-content')) {
        setExpandedState(true);
      } else {
        setExpandedState(false);
      }
    };

    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager]);

  useEffect(() => {
    if (restorationState) {
      setActiveIndex(restorationState.activeIndex as number);
    } else {
      setActiveIndex(0);
    }
  }, [restorationState]);

  const findActiveIndex = (m: KitchenSinkMenuItem[] | null, p: PageView | null): number => {
    if (m && p) {
      const result = m.findIndex(item => {
        return (
          p.page.toLowerCase().includes(item.label.toLowerCase()) ||
          item.tag?.name?.toLowerCase() === p.type
        );
      });
      return result;
    }
    return -1;
  };

  useEffect(() => {
    // if page changes, focus on the page we are currently on
    setActiveIndex(findActiveIndex(menu, page));
  }, [page, menu]);

  useEffect(() => {
    if (!fullScreenPlayer) {
      // only applies to hero player
      setVisibleState(true);
    } else {
      setVisibleState(false);
    }
  }, [fullScreenPlayer]);

  const onActionOverride = (action: string) => {
    if (action === appActions.moveRight) {
      // navigate away from left nav
      focusManager.setCurrentFocus(
        `${(pageName ?? '').toLowerCase()}-focusable-content`,
      );
    } else if (action === appActions.back) {
      dispatch(openExitDialog());
    }
  };

  const onFocusIndexChanged = (newid: string | number) => {
    setFocusIndex(newid as number);
  };

  // every time focus is sent to LeftNav, focus on the active page
  const onFocusSetOverrideFunction = (): number => {
    setExpandedState(true);
    return findActiveIndex(menu, page);
  };

  return (
    <div className={classNames('fixed-left', 'h-100')}>
      <div
        className={classNames(
          'fixed-left',
          'h-100',
          styles.navBackground,
          expandedState ? styles.expanded : styles.minimized,
          expandedState ? 'expanded' : 'minimized',
          visibleState ? 'visible' : 'd-none',
        )}
      />
      <div
        className={classNames(
          'fixed-left',
          'text-white',
          'h-100',
          styles.LeftNav,
          'd-flex flex-column justify-content-center align-items-center',
          expandedState ? styles.expanded : styles.minimized,
          expandedState ? 'expanded' : 'minimized',
          visibleState ? 'visible' : 'd-none',
        )}
      >
        {!!visibleState && (
          <FocusGroup
            horizontal={false}
            focusId="left-nav-focusable-content"
            onActionOverride={onActionOverride}
            onFocusSetOverride={onFocusSetOverrideFunction}
            onFocusIndexChanged={onFocusIndexChanged}
            className={classNames('w-100')}
          >
            {!loading &&
              !!menu &&
              menu.map((item, index) => (
                <NavItem
                  key={`nav-item-${item.tag ? item.tag.key : item.action}`}
                  class="nav-item"
                  item={item}
                  index={index}
                  pageName={pageName}
                  isActive={index === activeIndex}
                  isFocused={expandedState && index === focusIndex}
                  isExpanded={expandedState}
                />
              ))}
            {isActive() && !loading && !!menu && (
              <FeatureFlagsNavItem
                index={menu.length}
                isFocused={expandedState && focusIndex === menu.length}
                isExpanded={expandedState}
              />
            )}
          </FocusGroup>
        )}
      </div>
    </div>
  );
};

export default LeftNav;
